import { emailShapeValid, EMAIL_SHAPE_HINT, emailTypoSuggestion } from "./emailAddress.js";

// =============================================================================
// THE EMAIL-CHANGE DOOR — HER CLAUSE 3, READ LITERALLY: *"fix their email in account settings."*
// =============================================================================
//
// The signup-address round measured fifteen members whose address cannot receive mail, wrote the
// warm prompt that tells them so, and then had to point it at the CONTACT FORM — because there was
// no email-change control on this site at all. Its own note says the missing control is its own
// item. This is that item.
//
// **AND THE PROMPT'S COPY HAS BEEN PROMISING THIS FIELD SINCE THE DAY IT SHIPPED.**
// `UNDELIVERABLE_PROMPT_BODY` ends *"Check the spelling below and save it, and we will start
// reaching you again."* There was nothing below it and nothing to save — the sentence described a
// control that did not exist, which is A2's dead box wearing words instead of a button.
//
// -----------------------------------------------------------------------------------------------
// >>> WHY THIS IS A LAW AND NOT AN `UPDATE`, MEASURED BEFORE A LINE WAS WRITTEN (R25/R180). <<<
// -----------------------------------------------------------------------------------------------
//
// **THE SESSION COOKIE CARRIES THE ADDRESS AND NO USER ID** (`createSignedSession`), and
// `ensureUser` resolves a row by `google_sub` OR the address and then writes the SESSION'S address
// onto that row unconditionally. `resolveEntitlements` calls it on nearly every request. So the
// naive build — a route, an UPDATE, a panel control — ships a door that answers 200, paints
// *saved*, and is undone before she can reload, in one of two ways depending on who she is:
//
//   • **SHE SIGNS IN WITH GOOGLE (has a `sub`).** Her stale cookie still says the old address; the
//     next request matches her row by the sub and writes the OLD address straight back over the new
//     one. The change is REVERTED, silently, in milliseconds.
//   • **SHE SIGNS IN BY ADDRESS (no `sub`) — which is EVERY ONE OF THE FIFTEEN, measured.** Her
//     stale cookie matches no sub and no row (the old address is gone from the table), so
//     `ensureUser` CREATES A SECOND, EMPTY ACCOUNT under the old address and stands her in it. Her
//     room, her readings and her membership are orphaned on the row she just renamed.
//
// **SO THE SESSION IS RE-MINTED IN THE SAME RESPONSE AS THE WRITE, AND THAT IS THE LOAD-BEARING
// HALF OF THIS WHOLE ROUND.** It is one line at the door and it reads like a nicety; it is the only
// thing standing between her and a lost account. `email-change-mutations.mjs` M1 is the door
// without it.
//
// -----------------------------------------------------------------------------------------------
// >>> AND THE MEASUREMENT IS WHAT DECIDED WHICH ADDRESS THIS DOOR CHANGES. <<<
// -----------------------------------------------------------------------------------------------
//
// The tempting alternative is a SECOND column — a "contact address" beside the sign-in one — which
// touches no identity and cannot orphan anybody. It is refused, and the reason is R28/R29 on the
// one field this house mails: `MAILABLE_SQL`, every broadcast audience, the welcome mail, the card
// reminder and the funnel bridge all read `users.email`, so a second column makes every one of them
// choose, and the reader that misses it goes on mailing the dead address forever. Her own words are
// *fix their email*, not *add a second one*.
//
// **AND MIGRATION 039 ALREADY ANTICIPATED THIS DOOR IN AS MANY WORDS** — it matches on the Google
// subject *"so a sign-in whose email later changes still resolves to the same account."* The
// mechanism for a Google member was built years before the control; what it means is that for HER
// the address comes from Google and this house may not hold a change to it (below).
// =============================================================================

// WHICH KEY SIGNS HER IN. Three, never two (R18): the two real identities, and the fact that we
// have not established which — a read in flight is not a member with no account.
export const EMAIL_IDENTITY = { ADDRESS: "address", GOOGLE: "google", UNKNOWN: "unknown" };

export const EMAIL_CHANGE_ACTIONS = { CHANGE: "change", REFUSE: "refuse" };

// The reasons are a ROSTER because every one of them is a different next step for her, and because
// the ledger files under them — a door that refused for six different reasons under one name tells
// her owner nothing (C3's own finding, one door over).
export const EMAIL_CHANGE_REASONS = {
  OK: "ok",
  EMPTY: "empty",
  UNKNOWN_IDENTITY: "unknown-identity",
  ROSTER: "roster-identity",
  GOOGLE: "google-identity",
  UNCHANGED: "unchanged",
  INVALID: "invalid",
  TAKEN: "taken",
};

// >>> THE SENTENCES NAME WHAT IS TRUE AND NEVER THE MACHINERY (S1-A/R117). <<<
// No "roster", no "subject id", no "unique index", no "session". Each one ends on the thing she can
// do next, because a refusal with no door in it is a wall.
//
// **IT IS RAW ENGLISH BY THIS PANEL'S OWN CONVENTION AND THAT IS SAID OUT LOUD (R17/R160):**
// `UNDELIVERABLE_PROMPT_TITLE` and `UNDELIVERABLE_PROMPT_BODY` sit immediately above this control in
// the same menu and are raw English in `src/emailAddress.js`, so a translated panel here would read
// as two houses in one box. No `strings.js` row and no Spanish is owed.
export const EMAIL_CHANGE_COPY = {
  [EMAIL_CHANGE_REASONS.EMPTY]: "Type the address you would like us to use.",
  [EMAIL_CHANGE_REASONS.UNKNOWN_IDENTITY]: "We could not read your account just now. Try again in a moment.",
  [EMAIL_CHANGE_REASONS.ROSTER]:
    "Your access here is set up against this exact address, so it cannot be changed from this screen."
    + " Write to us and we will move it for you.",
  [EMAIL_CHANGE_REASONS.GOOGLE]:
    "You sign in with Google, so your address comes from your Google account."
    + " Change it there and it will follow you here the next time you sign in.",
  [EMAIL_CHANGE_REASONS.UNCHANGED]: "That is already the address we have for you.",
  [EMAIL_CHANGE_REASONS.TAKEN]: "That address already belongs to an account here.",
  [EMAIL_CHANGE_REASONS.OK]: "Saved. We will reach you at your new address from now on.",
};

export const EMAIL_CHANGE_TITLE = "Your email address";
export const EMAIL_CHANGE_SAVE_LABEL = "Save address";
// >>> M2 (her order, 2026-08-17) — **"WRITE TO US" READS "Have an issue?" IN THE MEMBER MENU. SAME
// DOOR, HER WORDING.** <<< It was a bare literal inside `EmailSettingsPanel`, which is the one place
// a label cannot be checked or translated, so it is a DECLARATION here beside the other two the
// panel reads. **Nothing about the door changed** — it is the same `onGoTo("contact")` it always
// was, which is the whole of her *same door* clause. Her question mark is hers and is kept: it is
// what turns a filing instruction into an offer of help.
export const EMAIL_CONTACT_LABEL = "Have an issue?";
// The panel is somewhere she chose to open, so it says what the address is FOR rather than only
// what it is — this is the line that tells a member with a working address why she would touch it.
export const EMAIL_CHANGE_LEDE =
  "This is where we send your readings, your reminders and anything Lala sends the house. It is also how you sign in.";

// One declaration of how this house folds an address, so the law, the store and the route cannot
// hold three opinions about whether two addresses are the same one (R28/R29). It agrees with
// `ensureUser`'s own `trim().toLowerCase()` and with the `lower(btrim(...))` the unique index is
// built on — and `emailShapeValid` forbids whitespace INSIDE an address, so no legal address can
// tell `btrim` and `String.trim()` apart.
export function foldAddress(email) {
  return String(email ?? "").trim().toLowerCase();
}

/**
 * >>> WHAT MAY HAPPEN TO THE ADDRESS ON HER ACCOUNT. <<<
 *
 * **EVERY DEFAULT REFUSES, AND ON THIS DOOR THAT IS NOT A FORMALITY.** PDF-1 shipped with every
 * default failing OPEN and the whole fence switched off for a caller who forgot an argument; here
 * the equivalent is an identity write performed for a caller who never established whose identity
 * it is. `taken` and `rosterHeld` default TRUE and `identity` defaults to UNKNOWN, so the failure
 * mode of forgetting an argument is a refused save with an honest sentence.
 *
 * **THE ORDER IS THE LAW AND IT IS HELD AS AN ORDER.** The identity refusals are asked ABOVE the
 * shape test, because telling a Google member that her address is malformed answers a question she
 * cannot act on — she cannot change it here at all, whatever she types. And `unchanged` is asked
 * above `invalid`, or a member whose address really is `josie@0825` is told her own address is not
 * a valid one when she has changed nothing.
 *
 * `taken` and `rosterHeld` arrive as PARAMETERS (CLOSET-1's shape, one door over), so this whole
 * law runs in a `.mjs` gate with no database and no network.
 */
export function emailChangeVerdict({
  current = "",
  next = "",
  identity = EMAIL_IDENTITY.UNKNOWN,
  rosterHeld = true,
  taken = true,
} = {}) {
  const address = foldAddress(next);
  const held = foldAddress(current);
  const refuse = (reason, extra = {}) => ({
    action: EMAIL_CHANGE_ACTIONS.REFUSE,
    reason,
    message: EMAIL_CHANGE_COPY[reason] || EMAIL_CHANGE_COPY[EMAIL_CHANGE_REASONS.UNKNOWN_IDENTITY],
    address: "",
    suggestion: "",
    ...extra,
  });

  if (!address) return refuse(EMAIL_CHANGE_REASONS.EMPTY);
  // Presence, never truthiness (R53/R136/R137). `rosterHeld` and `taken` come off a store read and
  // off JSON; `Boolean("false")` is `true`, and on this door a coerced answer is an account.
  if (rosterHeld !== false) return refuse(EMAIL_CHANGE_REASONS.ROSTER);
  if (identity === EMAIL_IDENTITY.GOOGLE) return refuse(EMAIL_CHANGE_REASONS.GOOGLE);
  if (identity !== EMAIL_IDENTITY.ADDRESS) return refuse(EMAIL_CHANGE_REASONS.UNKNOWN_IDENTITY);
  if (held && address === held) return refuse(EMAIL_CHANGE_REASONS.UNCHANGED);
  if (!emailShapeValid(address)) {
    // The suggester rides, because a member repairing `veronicayeager00@gmail` deserves the same
    // *did you mean* the signup field gives her — and three of the fifteen are exactly that shape.
    return refuse(EMAIL_CHANGE_REASONS.INVALID, {
      message: EMAIL_SHAPE_HINT,
      suggestion: emailTypoSuggestion(address),
    });
  }
  if (taken !== false) return refuse(EMAIL_CHANGE_REASONS.TAKEN);

  return {
    action: EMAIL_CHANGE_ACTIONS.CHANGE,
    reason: EMAIL_CHANGE_REASONS.OK,
    message: EMAIL_CHANGE_COPY[EMAIL_CHANGE_REASONS.OK],
    // **THE FOLDED ADDRESS TRAVELS BACK, AND THE CALLER WRITES EXACTLY THIS.** A route that re-folds
    // what the member typed is a second answer to what her address is, and the one that drifts is
    // the one the unique index is built on.
    address,
    suggestion: "",
  };
}

// WHAT THE PANEL DRAWS. Four faces, and `unknown` is first for `pushPanelState`'s own reason
// (P4-1): drawing the editable field while the read is still in flight invites a member to correct
// an address this house has not told her yet.
export const EMAIL_PANEL_STATES = {
  UNKNOWN: "unknown",
  GOOGLE: "google",
  ROSTER: "roster",
  EDITABLE: "editable",
};

export function emailPanelState({ known = false, identity = EMAIL_IDENTITY.UNKNOWN, rosterHeld = true } = {}) {
  if (known !== true) return EMAIL_PANEL_STATES.UNKNOWN;
  if (rosterHeld !== false) return EMAIL_PANEL_STATES.ROSTER;
  if (identity === EMAIL_IDENTITY.GOOGLE) return EMAIL_PANEL_STATES.GOOGLE;
  if (identity !== EMAIL_IDENTITY.ADDRESS) return EMAIL_PANEL_STATES.UNKNOWN;
  return EMAIL_PANEL_STATES.EDITABLE;
}

// The two faces that cannot edit still need a sentence, and it is the SAME one the verdict would
// give her — never a second wording written for the panel, or the door and the screen tell her two
// different stories about why (R28/R29).
export function emailPanelNote(state) {
  if (state === EMAIL_PANEL_STATES.GOOGLE) return EMAIL_CHANGE_COPY[EMAIL_CHANGE_REASONS.GOOGLE];
  if (state === EMAIL_PANEL_STATES.ROSTER) return EMAIL_CHANGE_COPY[EMAIL_CHANGE_REASONS.ROSTER];
  return "";
}

// >>> ONLY THE EDITABLE FACE GETS A FIELD AND A SAVE. <<< A2's law: a control she cannot act on does
// not render. A field drawn for a Google member is one she can type into, press, and watch answer
// *no* — which is worse than not offering it, because it reads as the house being broken rather
// than as her address living somewhere else.
export function emailPanelControls(state) {
  const editable = state === EMAIL_PANEL_STATES.EDITABLE;
  return { field: editable, save: editable, contact: state === EMAIL_PANEL_STATES.GOOGLE || state === EMAIL_PANEL_STATES.ROSTER };
}
